import { useState } from 'react';
import type { DocumentLocale, DocumentType } from '@/quotation/document/types';
import type { ScenarioKey } from '@/quotation/fixtures/quotationFixtures';
import { SCENARIO_LABELS } from '@/quotation/fixtures/quotationFixtures';

type PreviewMode = 'html' | 'pdf' | 'side-by-side';

interface Props {
  docType: DocumentType;
  locale: DocumentLocale;
  scenario: ScenarioKey;
  mode: PreviewMode;
  onDocTypeChange: (v: DocumentType) => void;
  onLocaleChange: (v: DocumentLocale) => void;
  onScenarioChange: (v: ScenarioKey) => void;
  onModeChange: (v: PreviewMode) => void;
}

const DOC_TYPE_OPTIONS: { value: DocumentType; label: string }[] = [
  { value: 'preliminary_quote', label: 'Preliminary Quotation' },
  { value: 'final_offer', label: 'Final Offer' },
];

const LOCALE_OPTIONS: { value: DocumentLocale; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'ko', label: '한국어' },
  { value: 'zh-CN', label: '中文 (简体)' },
];

const MODE_OPTIONS: { value: PreviewMode; label: string; icon: string }[] = [
  { value: 'html', label: 'HTML', icon: 'ri-code-s-slash-line' },
  { value: 'pdf', label: 'PDF', icon: 'ri-file-pdf-line' },
  { value: 'side-by-side', label: 'Side by Side', icon: 'ri-layout-column-line' },
];

function Select<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      className="w-full text-sm border border-stone-300 rounded-lg px-3 py-2 bg-white text-stone-700 focus:outline-none focus:ring-2 focus:ring-ink-400/20 focus:border-ink-500 transition-colors cursor-pointer"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export default function StudioControls({
  docType,
  locale,
  scenario,
  mode,
  onDocTypeChange,
  onLocaleChange,
  onScenarioChange,
  onModeChange,
}: Props) {
  const [open, setOpen] = useState(true);

  const scenarioOptions = Object.entries(SCENARIO_LABELS).map(([k, v]) => ({
    value: k as ScenarioKey,
    label: v,
  }));

  return (
    <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 md:px-6 py-4 hover:bg-stone-50 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-ink-700 rounded-lg">
            <i className="ri-settings-3-line text-white text-sm" />
          </div>
          <div className="text-left">
            <div className="text-sm font-semibold text-stone-900">Controls</div>
            <div className="text-[11px] text-stone-400">
              {DOC_TYPE_OPTIONS.find((o) => o.value === docType)?.label} &bull; {LOCALE_OPTIONS.find((o) => o.value === locale)?.label} &bull; {SCENARIO_LABELS[scenario]}
            </div>
          </div>
        </div>
        {open ? <i className="ri-arrow-up-s-line text-stone-400" /> : <i className="ri-arrow-down-s-line text-stone-400" />}
      </button>

      {open && (
        <div className="border-t border-stone-100 px-4 md:px-6 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-[11px] font-bold text-stone-500 uppercase tracking-wider mb-1.5">
                Document Type
              </label>
              <Select
                value={docType}
                onChange={onDocTypeChange}
                options={DOC_TYPE_OPTIONS}
              />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-stone-500 uppercase tracking-wider mb-1.5">
                Language
              </label>
              <Select value={locale} onChange={onLocaleChange} options={LOCALE_OPTIONS} />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-stone-500 uppercase tracking-wider mb-1.5">
                Mock Scenario
              </label>
              <Select
                value={scenario}
                onChange={onScenarioChange}
                options={scenarioOptions}
              />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-stone-500 uppercase tracking-wider mb-1.5">
                Preview Mode
              </label>
              <div className="flex gap-1">
                {MODE_OPTIONS.map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => onModeChange(m.value)}
                    title={m.label}
                    className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-lg border text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      mode === m.value
                        ? 'bg-ink-700 text-white border-ink-700'
                        : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-50'
                    }`}
                  >
                    <i className={m.icon} />
                    <span className="hidden md:inline">{m.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}